import { useState } from 'react';
import { useLocation, Link } from 'wouter';
import { useAuthStore } from '../hooks/useAuth';
import { api, ApiError } from '../lib/api';
import {
    isLegalDocsPublished,
    isLegalAcceptanceRequired,
    getPublishedTermsVersion,
    getPublishedPrivacyVersion,
} from '../config/legalConfig';

export function CarrierSignup() {
    const [, setLocation] = useLocation();
    const { login } = useAuthStore();
    const [formData, setFormData] = useState({
        companyName: '',
        mcNumber: '',
        email: '',
        password: '',
        confirmPassword: '',
    });
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [mcConflict, setMcConflict] = useState(false);
    const [recovering, setRecovering] = useState(false);
    const legalAcceptanceRequired = isLegalAcceptanceRequired();

    const validatePassword = (password: string, email: string, companyName: string) => {
        if (password.length < 10) return "Password must be at least 10 characters.";
        if (!/[A-Z]/.test(password)) return "Password must include at least 1 uppercase letter.";
        if (!/[a-z]/.test(password)) return "Password must include at least 1 lowercase letter.";
        if (!/[0-9]/.test(password)) return "Password must include at least 1 number.";
        if (!/[^\w\s]/.test(password)) return "Password must include at least 1 special character.";
        if (/\s/.test(password)) return "Password cannot contain spaces.";

        const pwLower = password.toLowerCase();
        const localPart = email.split('@')[0]?.toLowerCase();

        if (localPart && pwLower.includes(localPart)) {
            return "Password cannot include part of your email.";
        }

        if (companyName) {
            // Tokenize: split on non-alphanumeric
            const tokens = companyName.toLowerCase().split(/[^a-z0-9]+/);
            // Filter: length >= 4 and not common
            const common = new Set(['llc', 'inc', 'ltd', 'co', 'corp', 'company']);
            const significantTokens = tokens.filter(t => t.length >= 4 && !common.has(t));

            for (const token of significantTokens) {
                if (pwLower.includes(token)) {
                    return "Password cannot include parts of your company name.";
                }
            }
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        const passwordError = validatePassword(formData.password, formData.email, formData.companyName);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        // Email format validation
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            setError('Invalid email format');
            return;
        }

        if (legalAcceptanceRequired && !acceptedTerms) {
            setError('You must accept the Terms of Service and acknowledge the Privacy Policy to create an account.');
            return;
        }

        setLoading(true);

        try {
            const data = await api.carrierRegister({
                email: formData.email,
                password: formData.password,
                companyName: formData.companyName,
                mcNumber: formData.mcNumber,
                ...(legalAcceptanceRequired
                    ? {
                          legalAcceptance: {
                              acceptedTerms: true,
                              acknowledgedPrivacy: true,
                              termsVersion: getPublishedTermsVersion(),
                              privacyVersion: getPublishedPrivacyVersion(),
                          },
                      }
                    : {}),
            });

            // Store token and user data
            const response = data as any;
            login(response.user, response.token);
            setLocation('/carrier-dashboard');
        } catch (err: any) {
            if (err instanceof ApiError && (err.code === 'MC_NUMBER_IN_USE' || err.code === 'CARRIER_ACCOUNT_EXISTS' || err.status === 409)) {
                setMcConflict(true);
            }
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRecover = async () => {
        setRecovering(true);
        try {
            await api.carrierRecover(formData.mcNumber);
            setError(null);
            setMcConflict(false);
            alert('If an account exists with that MC number, we sent a recovery email.');
        } catch {
            alert('Something went wrong. Please try again.');
        } finally {
            setRecovering(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 py-12">
            <div className="w-full max-w-md space-y-6">

                {/* Header */}
                <div className="text-center space-y-2">
                    <Link href="/" className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-primary transition-colors mb-4">
                        ← Back to Home
                    </Link>
                    <div className="flex justify-center">
                        <span className="text-4xl filter drop-shadow-sm">🚚</span>
                    </div>
                    <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                        Carrier Sign Up
                    </h2>
                    <p className="text-sm text-slate-500">
                        Create an account for your trucking or logistics company
                    </p>
                </div>

                {/* Card */}
                <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-xl">
                    {error && (
                        <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                            <p>{error}</p>
                            {mcConflict && (
                                <button
                                    type="button"
                                    onClick={handleRecover}
                                    disabled={recovering}
                                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 hover:underline disabled:opacity-50"
                                >
                                    {recovering ? 'Sending...' : '🔑 Recover access to your existing account'}
                                </button>
                            )}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-1">
                            <label className="text-sm font-medium text-slate-700">Carrier Company Name <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                value={formData.companyName}
                                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                                placeholder="e.g., Summit Freight LLC"
                                required
                            />
                        </div>

                        <p className="text-xs font-medium text-slate-400 uppercase tracking-wider pt-2 border-t border-slate-200">Your Contact Details</p>

                        <div className="space-y-1">
                            <label className="text-sm font-medium text-slate-700">MC Number <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                value={formData.mcNumber}
                                onChange={(e) => setFormData({ ...formData, mcNumber: e.target.value })}
                                placeholder="e.g., MC-123456"
                                required
                                pattern="^(MC-)?\d{5,}$"
                                title="Format: MC- followed by 5+ digits (e.g., MC-123456)"
                            />
                            <p className="text-xs text-slate-400">Your DOT Motor Carrier number</p>
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium text-slate-700">Contact Email <span className="text-red-500">*</span></label>
                            <input
                                type="email"
                                className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="dispatch@yourcompany.com"
                                required
                                autoComplete="email"
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium text-slate-700">Password <span className="text-red-500">*</span></label>
                            <input
                                type="password"
                                className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder="At least 10 characters"
                                required
                                minLength={10}
                                autoComplete="new-password"
                            />
                            <p className="text-xs text-slate-400">
                                Must be 10+ chars, with uppercase, lowercase, number, and special char.
                            </p>
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium text-slate-700">Confirm Password <span className="text-red-500">*</span></label>
                            <input
                                type="password"
                                className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                placeholder="Repeat your password"
                                required
                                autoComplete="new-password"
                            />
                        </div>

                        {legalAcceptanceRequired && (
                            <div
                                data-testid="carrier-legal-acceptance-section"
                                className="rounded-xl border border-slate-200 bg-slate-50 p-3.5"
                            >
                                <label
                                    htmlFor="carrier-accept-legal"
                                    className="flex items-start gap-3 text-xs sm:text-sm text-slate-700 cursor-pointer"
                                >
                                    <input
                                        id="carrier-accept-legal"
                                        data-testid="carrier-accept-legal-checkbox"
                                        type="checkbox"
                                        checked={acceptedTerms}
                                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                                    />
                                    <span className="leading-relaxed">
                                        I confirm that I am authorized to register this carrier business (or that it is my own business), and on its behalf I agree to the{' '}
                                        <a
                                            href="/terms"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="carrier-terms-link"
                                            className="font-semibold text-primary hover:underline"
                                        >
                                            BayWhiz Terms of Service
                                        </a>{' '}
                                        (version <span data-testid="carrier-terms-version">{getPublishedTermsVersion()}</span>). I acknowledge that I have read the{' '}
                                        <a
                                            href="/privacy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="carrier-privacy-link"
                                            className="font-semibold text-primary hover:underline"
                                        >
                                            BayWhiz Privacy Policy
                                        </a>{' '}
                                        (version <span data-testid="carrier-privacy-version">{getPublishedPrivacyVersion()}</span>), including that my carrier profile is kept in BayWhiz&apos;s Global Carrier Registry and shared with the warehouses I book with.
                                    </span>
                                </label>
                            </div>
                        )}

                        <button
                            type="submit"
                            className="h-12 w-full bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold transition disabled:opacity-60"
                            disabled={loading}
                        >
                            {loading ? 'Creating Account...' : 'Create Account'}
                        </button>
                    </form>

                    <div className="mt-6 text-center text-sm space-y-2">
                        <p>
                            <span className="text-slate-500">Already have an account? </span>
                            <Link href="/carrier-login" className="font-medium text-primary hover:text-primary/80 transition-colors">
                                Sign in
                            </Link>
                        </p>
                        <p className="text-slate-500">
                            Are you a warehouse? <Link href="/request-trial" className="font-medium text-primary hover:text-primary/80 transition-colors">Request warehouse trial access →</Link>
                        </p>
                    </div>
                </div>

                {isLegalDocsPublished() && (
                    <footer
                        data-testid="carrier-signup-legal-footer"
                        className="pt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-slate-500"
                    >
                        <Link href="/terms" className="hover:text-primary hover:underline font-medium">
                            Terms of Service
                        </Link>
                        <span aria-hidden="true">·</span>
                        <Link href="/privacy" className="hover:text-primary hover:underline font-medium">
                            Privacy Policy
                        </Link>
                        <span aria-hidden="true">·</span>
                        <a href="mailto:support@baywhiz.com" className="hover:text-primary hover:underline">
                            support@baywhiz.com
                        </a>
                    </footer>
                )}
            </div>
        </div>
    );
}
export default CarrierSignup;
